// src/neetjee_guru/components/academicPlanner/PlannerPDFExport.js
// Uses window.print() with a dedicated print stylesheet injected at runtime.
// This approach works in ALL browsers with zero dependencies.
import React, { Component } from "react";

const SUBJECT_COLORS_HEX = {
  math:   "#8b5cf6",
  physic: "#3b82f6",
  chem:   "#10b981",
  botan:  "#059669",
  zoolog: "#f59e0b",
};

function getSubjectColor(subject) {
  const s = (subject || "").trim().toLowerCase();
  for (const [key, color] of Object.entries(SUBJECT_COLORS_HEX)) {
    if (s.includes(key)) return color;
  }
  return "#64748b";
}

class PlannerPDFExport extends Component {
  handlePrint = () => {
    const { filteredDays, activeSubjects, plannerName, streamName, dateFrom, dateTo } = this.props;

    if (!filteredDays || filteredDays.length === 0) {
      alert("No planner data to export. Please load planner details first.");
      return;
    }

    // Build subject list
    const allSubjects = [];
    filteredDays.forEach(day => {
      if (day.topics) {
        Object.keys(day.topics).forEach(s => {
          if (!allSubjects.includes(s)) allSubjects.push(s);
        });
      }
    });
    const subjectCols = allSubjects.length > 0 ? allSubjects : (activeSubjects || []);

    // Build table rows HTML
    let rowsHtml = "";
    filteredDays.forEach((day, i) => {
      const dateObj = new Date(day.date);
      const fmtDate = isNaN(dateObj.getTime())
        ? day.date
        : dateObj.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
      const dayName = isNaN(dateObj.getTime()) ? "" : dateObj.toLocaleDateString("en-IN", { weekday: "short" });
      const isSunday = dateObj.getDay() === 0;

      const bg = i % 2 === 0 ? "#ffffff" : "#f8fafc";
      const dayTypeBg = day.type === "Exam" ? "#eff6ff" : day.type === "Holiday" ? "#fef2f2" : "transparent";

      const subjectCells = subjectCols.map(sub => {
        const topic = (day.topics && day.topics[sub]) || "";
        const color = getSubjectColor(sub);
        return `<td style="padding:7px 10px; font-size:11px; border-right:1px solid #e2e8f0; max-width:160px; word-break:break-word; color:${topic ? "#1e293b" : "#cbd5e1"};">
          ${topic ? `<span style="border-left:3px solid ${color}; padding-left:6px; display:block; line-height:1.4;">${topic}</span>` : "—"}
        </td>`;
      }).join("");

      rowsHtml += `<tr style="background:${bg}; border-bottom:1px solid #e9ecef;">
        <td style="padding:7px 10px; text-align:center; font-weight:800; color:${isSunday ? "#ef4444" : "#1e1b4b"}; font-size:13px;">${day.dayNumber || i + 1}</td>
        <td style="padding:7px 10px; font-weight:600; white-space:nowrap; font-size:12px; color:${isSunday ? "#dc2626" : "#374151"};">${fmtDate}</td>
        <td style="padding:7px 10px; font-size:11px; color:${isSunday ? "#dc2626" : "#6b7280"}; font-weight:700;">${dayName}</td>
        <td style="padding:7px 10px; text-align:center; font-size:11px; color:#94a3b8; font-weight:600;">W${day.weekNumber || ""}</td>
        <td style="padding:7px 10px; font-size:11px; background:${dayTypeBg}; font-weight:700; color:${day.type === "Exam" ? "#1e40af" : day.type === "Holiday" ? "#991b1b" : "#065f46"};">${day.type === "Regular" ? "Lesson" : (day.type || "Lesson")}</td>
        ${subjectCells}
      </tr>`;
    });

    const subjectHeaders = subjectCols.map(sub => {
      const color = getSubjectColor(sub);
      return `<th style="padding:10px 12px; background:#1e1b4b; color:#fff; font-size:10px; text-transform:uppercase; letter-spacing:0.5px; border-right:1px solid rgba(255,255,255,0.12); border-left:3px solid ${color};">${sub}</th>`;
    }).join("");

    const dateRangeStr = filteredDays.length > 0
      ? `${filteredDays[0].date} — ${filteredDays[filteredDays.length - 1].date}`
      : "";

    const html = `<!DOCTYPE html>
<html lang="en">
<head>
<meta charset="UTF-8" />
<title>${plannerName || "Academic Planner"} – PDF Export</title>
<style>
  @page { size: A4 landscape; margin: 18mm 14mm; }
  * { box-sizing: border-box; }
  body { font-family: 'Segoe UI', Arial, sans-serif; color: #1e293b; background: #fff; margin: 0; padding: 0; }
  .header { display: flex; justify-content: space-between; align-items: flex-end; padding-bottom: 12px; border-bottom: 2.5px solid #1e1b4b; margin-bottom: 18px; }
  .header-left h1 { font-size: 22px; font-weight: 900; color: #1e1b4b; margin: 0 0 4px; }
  .header-left p { font-size: 12px; color: #64748b; margin: 0; }
  .header-right { text-align: right; font-size: 11px; color: #64748b; }
  .header-right .badge { display: inline-block; padding: 3px 10px; border-radius: 20px; background: #f1f5f9; color: #334155; font-weight: 700; font-size: 11px; margin-left: 6px; }
  table { width: 100%; border-collapse: collapse; font-size: 12px; }
  thead tr { background: linear-gradient(135deg, #1e1b4b 0%, #312e81 100%); }
  th { padding: 10px 12px; color: #fff; font-size: 10px; text-transform: uppercase; letter-spacing: 0.5px; font-weight: 700; border-right: 1px solid rgba(255,255,255,0.12); white-space: nowrap; }
  td { vertical-align: middle; }
  tr:nth-child(even) { background: #f8fafc; }
  .footer { margin-top: 16px; padding-top: 10px; border-top: 1px solid #e2e8f0; display: flex; justify-content: space-between; font-size: 10px; color: #94a3b8; }
  @media print { body { print-color-adjust: exact; -webkit-print-color-adjust: exact; } }
</style>
</head>
<body>
<div class="header">
  <div class="header-left">
    <h1>📅 ${plannerName || "Academic Master Planner"}</h1>
    <p>Stream: <strong>${streamName || "—"}</strong> &nbsp;·&nbsp; Period: <strong>${dateRangeStr}</strong> &nbsp;·&nbsp; Total: <strong>${filteredDays.length} days</strong></p>
  </div>
  <div class="header-right">
    Generated: ${new Date().toLocaleString("en-IN")}
    <br/><span class="badge">${subjectCols.length} Subjects</span>
  </div>
</div>
<table>
  <thead>
    <tr>
      <th style="width:44px;">Day #</th>
      <th style="width:100px;">Date</th>
      <th style="width:44px;">Day</th>
      <th style="width:40px;">Week</th>
      <th style="width:64px;">Type</th>
      ${subjectHeaders}
    </tr>
  </thead>
  <tbody>
    ${rowsHtml}
  </tbody>
</table>
<div class="footer">
  <span>Academic Master Planner — Rizee Education Platform</span>
  <span>Total: ${filteredDays.length} days scheduled · ${subjectCols.length} subjects tracked</span>
</div>
<script>window.onload = function() { window.print(); };<\/script>
</body>
</html>`;

    const win = window.open("", "_blank", "width=1400,height=900");
    if (!win) {
      alert("Popup blocked! Please allow popups for this site to export PDF.");
      return;
    }
    win.document.write(html);
    win.document.close();
  };

  render() {
    const { filteredDays } = this.props;
    const count = filteredDays ? filteredDays.length : 0;

    return (
      <button
        type="button"
        onClick={this.handlePrint}
        title={`Export ${count} days to PDF`}
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: 6,
          padding: "7px 16px",
          borderRadius: 10,
          border: "1.5px solid #dc2626",
          background: "#fff",
          color: "#dc2626",
          fontWeight: 700,
          fontSize: "13px",
          cursor: "pointer",
          transition: "all 0.18s",
          whiteSpace: "nowrap"
        }}
        onMouseEnter={e => { e.currentTarget.style.background = "#fef2f2"; }}
        onMouseLeave={e => { e.currentTarget.style.background = "#fff"; }}
      >
        <i className="fal fa-file-pdf" style={{ fontSize: 14 }} />
        Export PDF
        {count > 0 && (
          <span style={{
            background: "#dc2626", color: "#fff",
            borderRadius: 10, padding: "1px 7px",
            fontSize: "11px", fontWeight: 800
          }}>
            {count}
          </span>
        )}
      </button>
    );
  }
}

export default PlannerPDFExport;
